/**
 * Preview dev-only de /admin/consulta-vecinos (Consulta de vecinos, módulo
 * Comisión). Gate por import.meta.env.DEV (convención #15) — no accesible en
 * producción.
 *
 * Inyecta funciones mock (buscar + ficha) a la página real para validar la
 * interacción (búsqueda → resultados → ficha) sin backend ni auth. Los datos
 * son plausibles pero ficticios.
 */
import { ConsultaVecinos } from '../admin/ConsultaVecinos';
import type {
  CoincidenciaCasa,
  FichaVecino,
} from '../../lib/api/comision-vecinos';
import type { CasaAnualResumen, EstadoMes } from '../../lib/api/casas-tesoreria';

// Meses REALES de julio-2026 (2º semestre en curso) de las casas 9001 y 9002.
// Los meses del S2 sin pagar salen no_pagado (no futuro); "futuro" solo aplica
// a un semestre que aún no empieza (ej. mirando la grilla en mayo).
// 9001 (Atrasada): pagó S1 completo, debe todo el S2.
const MESES_9001: EstadoMes[] = [
  'pagado', 'pagado', 'pagado', 'pagado', 'pagado', 'pagado',
  'no_pagado', 'no_pagado', 'no_pagado', 'no_pagado', 'no_pagado', 'no_pagado',
];
// 9002 (Al día con saldo): pagó hasta agosto, debe sep-dic del S2.
const MESES_9002: EstadoMes[] = [
  'pagado', 'pagado', 'pagado', 'pagado', 'pagado', 'pagado',
  'pagado', 'pagado', 'no_pagado', 'no_pagado', 'no_pagado', 'no_pagado',
];

function mockCasa(
  identificador: string,
  titular: string,
  estado: CasaAnualResumen['estado'],
  saldoAHoy: number,
  ultimoPago: string | null,
  meses: EstadoMes[],
  extra?: Partial<CasaAnualResumen>,
): CasaAnualResumen {
  const deuda = saldoAHoy > 0 ? saldoAHoy : 0;
  return {
    lote_id: `lote-${identificador}`,
    identificador,
    sector: 'Poniente',
    titular_nombre: titular,
    titular_celular: '912345678',
    estado,
    saldo_total_pendiente: deuda,
    saldo_semestre_actual: deuda,
    saldo_a_favor: saldoAHoy < 0 ? -saldoAHoy : 0,
    saldo_a_hoy: saldoAHoy,
    deuda_anio_anterior: 0,
    abono_anio_anterior: 0,
    ultimo_pago_fecha: ultimoPago,
    meses,
    tiene_compensacion: false,
    ...extra,
  };
}

interface CasoMock {
  coincidencia: CoincidenciaCasa;
  ficha: FichaVecino;
  // Texto adicional por el cual debe matchear la búsqueda mock.
  buscables: string[];
}

// Dos casas 100% FICTICIAS para el smoke (números 9001/9002 fuera del rango
// real del barrio 6831-6991). Todo inventado — nombres, RUT, celular, patente,
// serial y el estado financiero. Nada real de vecino va al repo (doctrina de
// anonimización): el estado de morosidad de una casa individualizable también
// es dato personal, por eso ni los números de casa son reales. Las RELACIONES
// (llavero→persona, CR→vehículo→persona, bolsa) sí reflejan casos plausibles
// para ejercitar el reporte. 9001 = Atrasada; 9002 = Al día (con saldo).
const CASOS: CasoMock[] = [
  {
    // 9001 (Atrasada): datos personales anonimizados; financiero + estructura
    // + relaciones (llavero→persona, CR→vehículo→persona, llaveros en bolsa)
    // reflejan un caso real para ejercitar el reporte. Nada real de vecino.
    coincidencia: {
      lote_id: 'lote-9001',
      identificador: '9001',
      sector: 'Oriente',
      titular_nombre: 'Titular Ejemplo Oriente',
      motivo_match: 'casa',
      detalle_match: '9001',
    },
    buscables: ['9001', 'oriente', 'ejemplo', 'cccc-33', 'll-0210'],
    ficha: {
      casa: mockCasa(
        '9001', 'Titular Ejemplo Oriente', 'atrasada', 5000, '2026-01-01',
        MESES_9001,
        { saldo_semestre_actual: 30000, sector: 'Oriente' },
      ),
      estado_pago: 'atrasada',
      suspendida: false,
      personas: [
        {
          persona_id: 'p-1',
          nombre_completo: 'Titular Ejemplo Oriente',
          rut: '19000000-9',
          celular: '912340010',
          email: null,
          tipo_tenencia: 'propietario_residente',
          es_responsable_pago: true,
          onboarding_completado: true,
          portal_estado: 'activo',
          portal_ultimo_login: new Date(Date.now() - 3 * 3600_000).toISOString(),
          citofonia_slot: 1,
          citofonia_estado: 'activo',
          citofonia_programada: true,
          wifi_estado: 'activo',
          wifi_programado: true,
          wifi_estado_efectivo: 'activo',
        },
      ],
      residentes: [
        {
          persona_id: 'p-2',
          nombre_completo: 'Residente Ejemplo A',
          rut: '19100000-0',
          celular: '912340011',
          email: null,
          parentesco: 'hijo',
          portal_estado: 'sin_solicitud',
          portal_ultimo_login: null,
          citofonia_slot: 2,
          citofonia_estado: 'activo',
          citofonia_programada: false,
          wifi_estado: 'pendiente_validacion',
          wifi_programado: false,
          wifi_estado_efectivo: 'sin_solicitar',
        },
        {
          persona_id: 'p-3',
          nombre_completo: 'Residente Ejemplo B',
          rut: '19200000-1',
          celular: '912340012',
          email: null,
          parentesco: 'inquilino',
          portal_estado: 'sin_solicitud',
          portal_ultimo_login: null,
          citofonia_slot: null,
          citofonia_estado: null,
          citofonia_programada: false,
          wifi_estado: null,
          wifi_programado: false,
          wifi_estado_efectivo: 'sin_solicitar',
        },
      ],
      vehiculos: [
        {
          patente: 'CCCC-33', marca: 'Marca Ejemplo', modelo: null, color: 'Gris',
          tipo_traccion: 'combustion',
          control_remoto: 'CR-9001-01', asignado_a: 'Titular Ejemplo Oriente',
        },
        {
          patente: 'DDDD-44', marca: 'Marca Ejemplo', modelo: null, color: null,
          // mock: híbrido para ver el chip de tracción.
          tipo_traccion: 'hibrido',
          control_remoto: 'CR-9001-02', asignado_a: 'Residente Ejemplo B',
        },
      ],
      recursos: [
        { tipo: 'control_remoto', identificador: 'CR-9001-01', estado: 'entregado', serial: null, persona_id: 'p-1', asignado_a: 'Titular Ejemplo Oriente', vehiculo_patente: 'CCCC-33' },
        { tipo: 'control_remoto', identificador: 'CR-9001-02', estado: 'entregado', serial: null, persona_id: 'p-3', asignado_a: 'Residente Ejemplo B', vehiculo_patente: 'DDDD-44' },
        { tipo: 'llavero', identificador: 'LL-0210', estado: 'entregado', serial: '0000000210', persona_id: null, asignado_a: null, vehiculo_patente: null },
        { tipo: 'llavero', identificador: 'LL-0211', estado: 'entregado', serial: '0000000211', persona_id: 'p-1', asignado_a: 'Titular Ejemplo Oriente', vehiculo_patente: null },
        { tipo: 'llavero', identificador: 'LL-0212', estado: 'entregado', serial: '0000000212', persona_id: null, asignado_a: null, vehiculo_patente: null },
        { tipo: 'llavero', identificador: 'LL-0213', estado: 'entregado', serial: '0000000213', persona_id: null, asignado_a: null, vehiculo_patente: null },
        { tipo: 'llavero', identificador: 'LL-0214', estado: 'entregado', serial: '0000000214', persona_id: 'p-2', asignado_a: 'Residente Ejemplo A', vehiculo_patente: null },
      ],
    },
  },
  {
    // 9002: datos PERSONALES anonimizados (PII de otro vecino no va al repo,
    // doctrina de anonimización). El FINANCIERO (estado/meses/saldos) es real
    // y las RELACIONES (llavero→persona, CR→vehículo→persona, bolsa) reflejan
    // la estructura real para ejercitar el reporte.
    coincidencia: {
      lote_id: 'lote-9002',
      identificador: '9002',
      sector: 'Poniente',
      titular_nombre: 'Vecino Ejemplo Poniente',
      motivo_match: 'casa',
      detalle_match: '9002',
    },
    buscables: ['9002', 'poniente', 'ejemplo', 'aaaa-11', 'll-0201'],
    ficha: {
      casa: mockCasa(
        '9002', 'Vecino Ejemplo Poniente', 'al_dia_pendiente_semestre', 0,
        '2026-01-01',
        MESES_9002,
        { saldo_semestre_actual: 20000, sector: 'Poniente' },
      ),
      estado_pago: 'al_dia_pendiente_semestre',
      suspendida: false,
      personas: [
        {
          persona_id: 'q-1',
          nombre_completo: 'Vecino Ejemplo Poniente',
          rut: '15000000-5',
          celular: '912340000',
          email: null,
          tipo_tenencia: 'propietario_residente',
          es_responsable_pago: true,
          onboarding_completado: false,
          portal_estado: 'cuenta_sin_ingresar',
          portal_ultimo_login: null,
          citofonia_slot: 1,
          citofonia_estado: 'pendiente_validacion',
          citofonia_programada: false,
          wifi_estado: null,
          wifi_programado: false,
          wifi_estado_efectivo: 'sin_solicitar',
        },
      ],
      residentes: [
        {
          persona_id: 'q-2',
          nombre_completo: 'Residente Ejemplo Uno',
          rut: '16000000-6',
          celular: '912340001',
          email: null,
          parentesco: 'conyuge',
          portal_estado: 'activo',
          portal_ultimo_login: new Date(Date.now() - 26 * 3600_000).toISOString(),
          citofonia_slot: 2,
          citofonia_estado: 'activo',
          citofonia_programada: true,
          wifi_estado: 'activo',
          wifi_programado: true,
          wifi_estado_efectivo: 'activo',
        },
        {
          persona_id: 'q-3',
          nombre_completo: 'Residente Ejemplo Dos',
          rut: '17000000-7',
          celular: '912340002',
          email: null,
          parentesco: 'hijo',
          portal_estado: 'cuenta_inactiva',
          portal_ultimo_login: '2026-03-02T13:05:00Z',
          citofonia_slot: null,
          citofonia_estado: null,
          citofonia_programada: false,
          wifi_estado: 'activo',
          wifi_programado: false,
          wifi_estado_efectivo: 'por_activar',
        },
        {
          persona_id: 'q-4',
          nombre_completo: 'Residente Ejemplo Tres',
          rut: '18000000-8',
          celular: '912340003',
          email: null,
          parentesco: 'hijo',
          portal_estado: 'aprobada_sin_cuenta',
          portal_ultimo_login: null,
          citofonia_slot: null,
          citofonia_estado: null,
          citofonia_programada: false,
          wifi_estado: null,
          wifi_programado: false,
          wifi_estado_efectivo: 'sin_solicitar',
        },
      ],
      vehiculos: [
        {
          patente: 'AAAA-11', marca: 'Marca Ejemplo', modelo: null, color: 'Gris',
          tipo_traccion: 'combustion',
          control_remoto: 'CR-9002-01', asignado_a: 'Residente Ejemplo Uno',
        },
        {
          patente: 'BBBB-22', marca: 'Marca Ejemplo', modelo: null, color: 'Blanco',
          tipo_traccion: 'combustion',
          control_remoto: 'CR-9002-03', asignado_a: 'Vecino Ejemplo Poniente',
        },
      ],
      recursos: [
        { tipo: 'llavero', identificador: 'LL-0201', estado: 'entregado', serial: '0000000201', persona_id: 'q-2', asignado_a: 'Residente Ejemplo Uno', vehiculo_patente: null },
        { tipo: 'llavero', identificador: 'LL-0202', estado: 'entregado', serial: '0000000202', persona_id: 'q-1', asignado_a: 'Vecino Ejemplo Poniente', vehiculo_patente: null },
        { tipo: 'llavero', identificador: 'LL-0203', estado: 'entregado', serial: '0000000203', persona_id: 'q-4', asignado_a: 'Residente Ejemplo Tres', vehiculo_patente: null },
        { tipo: 'llavero', identificador: 'LL-0204', estado: 'entregado', serial: '0000000204', persona_id: 'q-3', asignado_a: 'Residente Ejemplo Dos', vehiculo_patente: null },
        { tipo: 'control_remoto', identificador: 'CR-9002-01', estado: 'entregado', serial: null, persona_id: 'q-2', asignado_a: 'Residente Ejemplo Uno', vehiculo_patente: 'AAAA-11' },
        { tipo: 'control_remoto', identificador: 'CR-9002-02', estado: 'entregado', serial: null, persona_id: null, asignado_a: null, vehiculo_patente: null },
        { tipo: 'control_remoto', identificador: 'CR-9002-03', estado: 'entregado', serial: null, persona_id: 'q-1', asignado_a: 'Vecino Ejemplo Poniente', vehiculo_patente: 'BBBB-22' },
      ],
    },
  },
];

function buscarMock(q: string): Promise<CoincidenciaCasa[]> {
  const t = q.trim().toLowerCase();
  const r = CASOS.filter((c) =>
    c.buscables.some((b) => b.toLowerCase().includes(t)),
  ).map((c) => c.coincidencia);
  return Promise.resolve(r);
}

function fichaMock(loteId: string): Promise<FichaVecino> {
  const caso = CASOS.find((c) => c.coincidencia.lote_id === loteId);
  if (!caso) return Promise.reject(new Error('no encontrada'));
  return Promise.resolve(caso.ficha);
}

export function ConsultaVecinosPreview() {
  return (
    <div className="min-h-screen bg-bg-page text-text-primary">
      <header className="bg-orange px-4 md:px-8 py-4 flex items-center justify-between">
        <span className="text-base font-medium text-white tracking-widest">
          CALBUCURA · Comisión
        </span>
        <span className="text-xs text-white/80">Preview dev-only</span>
      </header>
      <main className="max-w-3xl mx-auto">
        <ConsultaVecinos buscarFn={buscarMock} fichaFn={fichaMock} />
      </main>
    </div>
  );
}
